import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { ArrowRight, Download, Sparkles } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function LeadMagnet() {
  const [form, setForm] = useState({ first_name: "", last_name: "", email: "" });
  const [submitting, setSubmitting] = useState(false);
  const [success, setSuccess] = useState(null); // {download_url, message}

  const onChange = (e) => setForm({ ...form, [e.target.name]: e.target.value });

  const onSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      const res = await axios.post(`${API}/lead-magnet/signup`, form);
      toast.success(res.data.message);
      setSuccess({
        download_url: `${process.env.REACT_APP_BACKEND_URL}${res.data.download_url}`,
        message: res.data.message,
        already: res.data.already_subscribed,
      });
    } catch (err) {
      const detail = err?.response?.data?.detail;
      toast.error(typeof detail === "string" ? detail : "Please check your details and try again.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <section
      data-testid="lead-magnet-section"
      id="free-guide"
      className="relative bg-gradient-to-br from-bone via-surface to-[#EFE9E2] py-24 md:py-36 overflow-hidden border-y border-sage/20"
    >
      {/* Decorative dot grid */}
      <div className="absolute inset-0 opacity-[0.06] pointer-events-none" style={{
        backgroundImage: "radial-gradient(#3B2E5A 1px, transparent 1px)",
        backgroundSize: "28px 28px",
      }} />

      <div className="relative max-w-7xl mx-auto px-6 md:px-12 grid grid-cols-1 lg:grid-cols-12 gap-14 lg:gap-20 items-center">
        {/* Left: copy + PDF mockup */}
        <div className="lg:col-span-7">
          <p className="overline mb-5 inline-flex items-center gap-2">
            <Sparkles size={12} strokeWidth={1.5} /> Free Guide
          </p>
          <h2 className="font-display text-4xl md:text-6xl text-forest font-medium leading-[1.05] tracking-tight">
            5 Days to <em className="italic text-terracotta">Calm</em> an Overthinking Mind.
          </h2>
          <p className="mt-6 text-lg text-ink/75 max-w-xl leading-relaxed">
            A short, kind, and practical guide written by our clinical team. Ten minutes a day — five days
            to soften the loop, anchor your body, and come back to yourself.
          </p>

          <ul className="mt-8 space-y-3 text-ink/80">
            {[
              "Evidence-based regulation techniques (physiological sigh, defusion)",
              "Daily practice + evening reflection prompts",
              "Short, kind, and practical. Just what helps.",
            ].map((b) => (
              <li key={b} className="flex items-start gap-3">
                <span className="mt-2 h-1.5 w-1.5 rounded-full bg-terracotta shrink-0" />
                <span>{b}</span>
              </li>
            ))}
          </ul>

          {/* PDF preview mockup */}
          <div className="mt-12 relative max-w-md">
            <div className="absolute -top-3 -left-3 w-full h-full bg-sage/30 rounded-2xl rotate-[-3deg]" />
            <div className="absolute -top-1.5 -left-1.5 w-full h-full bg-terracotta/30 rounded-2xl rotate-[-1.5deg]" />
            <div className="relative bg-forest text-bone rounded-2xl p-8 md:p-10 shadow-2xl">
              <p className="overline text-terracotta mb-4">Seven Pillars of Life</p>
              <p className="font-display text-3xl md:text-4xl leading-[1.1] font-medium">
                5 Days to Calm an <em className="italic text-terracotta">Overthinking Mind</em>
              </p>
              <div className="mt-8 pt-6 border-t border-bone/15 text-xs text-bone/55 flex justify-between">
                <span>Free Reflection Guide</span>
                <span>PDF</span>
              </div>
            </div>
          </div>
        </div>

        {/* Right: form */}
        <div className="lg:col-span-5">
          {!success ? (
            <form
              onSubmit={onSubmit}
              className="bg-surface border border-sage/30 rounded-2xl p-8 md:p-10 shadow-xl"
              data-testid="lead-magnet-form"
            >
              <h3 className="font-display text-2xl md:text-3xl text-forest font-medium mb-2">Send me the guide</h3>
              <p className="text-ink/65 text-sm mb-7">We'll email it instantly. No spam, ever — unsubscribe anytime.</p>

              <div className="space-y-4">
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="text-xs uppercase tracking-widest text-ink/60 font-semibold">First Name *</label>
                    <input
                      required
                      name="first_name"
                      value={form.first_name}
                      onChange={onChange}
                      data-testid="lm-firstname-input"
                      className="mt-2 w-full bg-bone border border-sage/30 rounded-xl px-4 py-3 focus:outline-none focus:border-forest text-ink"
                    />
                  </div>
                  <div>
                    <label className="text-xs uppercase tracking-widest text-ink/60 font-semibold">Last Name</label>
                    <input
                      name="last_name"
                      value={form.last_name}
                      onChange={onChange}
                      data-testid="lm-lastname-input"
                      className="mt-2 w-full bg-bone border border-sage/30 rounded-xl px-4 py-3 focus:outline-none focus:border-forest text-ink"
                    />
                  </div>
                </div>

                <div>
                  <label className="text-xs uppercase tracking-widest text-ink/60 font-semibold">Email *</label>
                  <input
                    required
                    type="email"
                    name="email"
                    value={form.email}
                    onChange={onChange}
                    data-testid="lm-email-input"
                    className="mt-2 w-full bg-bone border border-sage/30 rounded-xl px-4 py-3 focus:outline-none focus:border-forest text-ink"
                  />
                </div>
              </div>

              <button
                type="submit"
                disabled={submitting}
                data-testid="lm-submit-btn"
                className="mt-7 w-full inline-flex items-center justify-center gap-2 bg-terracotta hover:bg-terracottaDark text-bone rounded-full px-8 py-4 font-medium transition-colors disabled:opacity-60"
              >
                {submitting ? "Sending..." : (<>Download Your Free Gift <ArrowRight size={16} /></>)}
              </button>

              <p className="mt-4 text-xs text-ink/50 text-center">
                By signing up, you agree to receive occasional emails from Seven Pillars of Life Therapy.
              </p>
            </form>
          ) : (
            <div
              className="bg-forest text-bone rounded-2xl p-8 md:p-10 shadow-xl"
              data-testid="lm-success-card"
            >
              <p className="overline text-terracotta mb-4">{success.already ? "Welcome back" : "You're in"}</p>
              <h3 className="font-display text-3xl md:text-4xl font-medium leading-tight">
                Your guide is ready.
              </h3>
              <p className="mt-4 text-bone/80 leading-relaxed">{success.message}</p>
              <a
                href={success.download_url}
                target="_blank"
                rel="noopener noreferrer"
                data-testid="lm-download-btn"
                className="mt-8 inline-flex items-center gap-2 bg-terracotta hover:bg-terracottaDark text-bone rounded-full px-7 py-3.5 font-medium transition-colors"
              >
                <Download size={16} /> Access Your Free Reflection Guide
              </a>
              <p className="mt-4 text-xs text-bone/60">Bookmark this link — you can also grab it from your inbox.</p>
            </div>
          )}
        </div>
      </div>
    </section>
  );
}
